import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Avatar, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, CircularProgress, Stack,
} from '@mui/material';
import {
  Business as ClinicIcon, ArrowBack as BackIcon, LocalHospital as ProviderIcon,
  People as PeopleIcon, Send as SendIcon, Paid as CreditsIcon, Science as ProtocolIcon,
  Login as EnterIcon, PersonAdd as AddAdminIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ROLE_LABEL = { clinic_admin: 'Admin', provider: 'Provider', staff: 'Staff' };

export default function ClinicDetail() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [savingProtocol, setSavingProtocol] = useState(false);
  const [savingAddPatients, setSavingAddPatients] = useState(false);
  const [entering, setEntering] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState(null);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [addAdminForm, setAddAdminForm] = useState({ full_name: '', email: '' });
  const [savingAdmin, setSavingAdmin] = useState(false);

  const orgWord = (clinic?.type || 'clinic') === 'hospital' ? 'hospital' : 'clinic';

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API_URL}/api/v1/superadmin/clinics/${clinicId}`),
        axios.get(`${API_URL}/api/v1/superadmin/clinics/${clinicId}/members`),
      ]);
      setClinic(c.data);
      setMembers(m.data.members || []);
    } catch {
      toast.error('Failed to load clinic');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  const toggleProtocol = async (enabled) => {
    setSavingProtocol(true);
    setClinic((c) => ({ ...c, protocol_development_enabled: enabled }));   // optimistic
    try {
      await axios.patch(`${API_URL}/api/v1/superadmin/clinics/${clinicId}/protocol-development`, { enabled });
      toast.success(enabled ? 'Protocol Development enabled' : 'Protocol Development disabled');
    } catch (err) {
      setClinic((c) => ({ ...c, protocol_development_enabled: !enabled }));
      toast.error(err.response?.data?.detail || 'Could not update entitlement');
    } finally {
      setSavingProtocol(false);
    }
  };

  const toggleProvidersCanAddPatients = async (enabled) => {
    setSavingAddPatients(true);
    setClinic((c) => ({ ...c, providers_can_add_patients: enabled }));     // optimistic
    try {
      await axios.patch(`${API_URL}/api/v1/superadmin/clinics/${clinicId}/providers-can-add-patients`, { enabled });
      toast.success(enabled ? 'Providers can now add patients' : 'Only clinic admins can add patients');
    } catch (err) {
      setClinic((c) => ({ ...c, providers_can_add_patients: !enabled }));
      toast.error(err.response?.data?.detail || 'Could not update setting');
    } finally {
      setSavingAddPatients(false);
    }
  };

  const handleResendInvite = async (adminId) => {
    setResendingId(adminId);
    try {
      const res = await axios.post(
        `${API_URL}/api/v1/superadmin/clinics/${clinicId}/admins/${adminId}/resend-invite`
      );
      toast.success(res.data?.message || 'Invite re-sent');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResendingId(null);
    }
  };

  // Opens the clinic portal in a new tab on a one-time code, leaving this
  // platform-admin session untouched in the current tab.
  const enterClinic = async () => {
    setEntering(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/superadmin/clinics/${clinicId}/enter`);
      window.open(res.data.entry_url, '_blank', 'noopener');
      toast.success(`Opened ${clinic?.name} in a new tab`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not enter this clinic');
    } finally {
      setEntering(false);
    }
  };

  const setAdminRole = async (member, grant) => {
    setBusyMemberId(member.id);
    try {
      const res = await axios.put(
        `${API_URL}/api/v1/superadmin/clinics/${clinicId}/members/${member.id}/clinic-admin`,
        { grant }
      );
      toast.success(res.data?.message || 'Updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update their role');
    } finally {
      setBusyMemberId(null);
    }
  };

  const createAdmin = async () => {
    setSavingAdmin(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/superadmin/clinics/${clinicId}/admins`, {
        full_name: addAdminForm.full_name,
        email: addAdminForm.email,
      });
      toast.success(res.data?.message || 'Invitation sent');
      setAddAdminOpen(false);
      setAddAdminForm({ full_name: '', email: '' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add an administrator');
    } finally {
      setSavingAdmin(false);
    }
  };

  if (loading) return <Typography>Loading…</Typography>;
  if (!clinic) return <Typography>Clinic not found.</Typography>;

  const admins = members.filter((m) => m.is_clinic_admin);

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/clinics')} sx={{ mb: 2 }}>
        Back to Clinics
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><ClinicIcon fontSize="large" /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>{clinic.name}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip size="small" label={clinic.type || 'clinic'} />
            <Chip size="small" label={clinic.is_active ? 'Active' : 'Inactive'} color={clinic.is_active ? 'success' : 'default'} />
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ ml: 'auto' }}>
          <Tooltip title={clinic.is_active
            ? `Work inside this ${orgWord} with full administrator access`
            : `Reactivate this ${orgWord} before entering it`}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={entering ? <CircularProgress size={16} color="inherit" /> : <EnterIcon />}
                onClick={enterClinic}
                disabled={entering || !clinic.is_active}
              >
                {entering ? 'Opening…' : `Enter ${orgWord}`}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<CreditsIcon />} onClick={() => navigate(`/credits?clinic=${clinicId}`)}>
            Add AI Credits
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[['Address', clinic.address], ['Phone', clinic.phone], ['Email', clinic.email]].map(([label, value]) => (
          <Grid item xs={12} md={4} key={label}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography>{value || '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Feature entitlements */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: '#0d948820', color: '#0d9488' }}><ProtocolIcon /></Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700}>Protocol Development</Typography>
              <Typography variant="body2" color="text.secondary">
                AI-assisted clinical protocol authoring. When on, this clinic's providers and admins can
                build, version and publish protocols. Off by default.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!clinic.protocol_development_enabled}
                  disabled={savingProtocol}
                  onChange={(e) => toggleProtocol(e.target.checked)}
                />
              }
              label={clinic.protocol_development_enabled ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
            <Avatar sx={{ bgcolor: '#0d948820', color: '#0d9488' }}><PeopleIcon /></Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700}>Providers can add patients</Typography>
              <Typography variant="body2" color="text.secondary">
                When on, providers (not just clinic admins) can add patients. A clinic admin can also
                set this from clinic settings; this lets a super-admin toggle it too.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!clinic.providers_can_add_patients}
                  disabled={savingAddPatients}
                  onChange={(e) => toggleProvidersCanAddPatients(e.target.checked)}
                />
              }
              label={clinic.providers_can_add_patients ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
            />
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          [clinic.providers?.length ?? 0, 'Providers', <ProviderIcon />, '#2563eb'],
          [clinic.patient_count ?? 0, 'Patients', <PeopleIcon />, '#10b981'],
          [admins.length, 'Administrators', <AdminIcon />, '#f59e0b'],
        ].map(([count, label, icon, color]) => (
          <Grid item xs={12} sm={4} key={label}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${color}20`, color }}>{icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{count}</Typography>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Team & roles — one place to see who's here and who can administer it. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Team & roles</Typography>
        <Button size="small" variant="outlined" startIcon={<AddAdminIcon />}
          onClick={() => setAddAdminOpen(true)} sx={{ ml: 'auto' }}>
          Add administrator
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Sign-in</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Administrator</TableCell>
              <TableCell align="right">Invite</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  Nobody here yet. Add an administrator, or enter the {orgWord} to add
                  providers and staff.
                </TableCell>
              </TableRow>
            ) : members.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{m.full_name || m.email}</Typography>
                  {!m.full_name && (
                    <Typography variant="caption" color="text.secondary">name not set yet</Typography>
                  )}
                </TableCell>
                <TableCell><Typography variant="body2">{m.email}</Typography></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {m.roles.map((r) => (
                      <Chip key={r} size="small" label={ROLE_LABEL[r] || r}
                        color={r === 'clinic_admin' ? 'warning' : 'default'} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  {m.invite_pending ? (
                    <Chip size="small" label="Pending invite" color="warning" />
                  ) : (
                    <Chip size="small" label={m.is_active ? 'Active' : 'Inactive'}
                      color={m.is_active ? 'success' : 'default'} />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={m.is_clinic_admin
                    ? `Remove administrator rights for this ${orgWord}`
                    : `Let them administer this ${orgWord} — their existing roles are kept`}>
                    <span>
                      <Switch
                        size="small"
                        checked={m.is_clinic_admin}
                        disabled={busyMemberId === m.id}
                        onChange={(e) => setAdminRole(m, e.target.checked)}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {m.invite_pending && m.is_clinic_admin ? (
                    <Button size="small" startIcon={<SendIcon fontSize="small" />}
                      disabled={resendingId === m.id}
                      onClick={() => handleResendInvite(m.id)}>
                      {resendingId === m.id ? 'Sending…' : 'Resend invite'}
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Providers table */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Providers</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(clinic.providers || []).length === 0 ? (
              <TableRow><TableCell colSpan={3}>No providers assigned.</TableCell></TableRow>
            ) : clinic.providers.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell><Typography fontWeight={600}>{p.full_name || p.email}</Typography></TableCell>
                <TableCell>{p.email}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? 'success' : 'default'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add administrator */}
      <Dialog open={addAdminOpen} onClose={() => { if (!savingAdmin) setAddAdminOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add an administrator to {clinic.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            To promote somebody who is already here, use the Administrator switch in the
            table instead — this creates a brand-new account.
          </Typography>
          <TextField fullWidth label="Full name" value={addAdminForm.full_name} margin="normal"
            onChange={(e) => setAddAdminForm({ ...addAdminForm, full_name: e.target.value })} />
          <TextField fullWidth label="Email" value={addAdminForm.email} margin="normal"
            onChange={(e) => setAddAdminForm({ ...addAdminForm, email: e.target.value })}
            helperText="They're emailed a single-use link, valid 48h, to set their own password." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddAdminOpen(false)} disabled={savingAdmin}>Cancel</Button>
          <Button variant="contained" onClick={createAdmin}
            disabled={savingAdmin || !addAdminForm.email || !addAdminForm.full_name}
            startIcon={savingAdmin ? <CircularProgress size={16} color="inherit" /> : null}>
            {savingAdmin ? 'Sending…' : 'Send invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
